import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Space,
  Input,
  Select,
  Modal,
  Form,
  message,
  Tag,
  Popconfirm,
  Popover,
  Row,
  Col,
  Tooltip,
  Tabs,
  AutoComplete,
  Upload,
  Dropdown,
  Progress,
  Spin,
} from 'antd';
import dayjs from 'dayjs';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  SyncOutlined,
  EyeOutlined,
  TagOutlined,
  TranslationOutlined,
  DownloadOutlined,
  UploadOutlined,
  FileExcelOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import trademarkService, { Trademark, TrademarkListParams } from '../services/trademark';
import { useAuth } from '../contexts/AuthContext';
import tagService from '../services/tag';
import statusTranslationService from '../services/statusTranslation';
import {
  getTrademarkUrl,
  getTrademarkOfficeName,
  MONITORED_NATION_OPTIONS,
} from '../utils/trademarkUrls';
import ColumnSelector from '../components/ColumnSelector';
import ListHelpButton from '../components/ListHelpButton';
import userPreferenceService from '../services/userPreference';
import { usePermissions } from '../hooks/usePermissions';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { normalizeTrademarkStatus } from '../utils/trademarkStatus';
import { buildTrademarkTagNames } from '../utils/trademarkTags';
import type {Order} from "../services/order";

const { Option } = Select;

const handleCopyText = (text: string, e: React.MouseEvent) => {
  e.stopPropagation();
  if (!text) return;
  if (navigator.clipboard) {
    navigator.clipboard.writeText(text).then(() => {
      message.success('复制成功');
    }).catch(() => {
      message.error('复制失败');
    });
  } else {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
      message.success('复制成功');
    } catch (err) {
      message.error('复制失败');
    }
    document.body.removeChild(textarea);
  }
};

const TrademarkMonitor: React.FC = () => {
  useDocumentTitle('商标监控');
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const [trademarks, setTrademarks] = useState<Trademark[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncingStatus, setSyncingStatus] = useState(false);

  // 从 URL 参数初始化分页状态
  const [pagination, setPagination] = useState({
    current: parseInt(urlSearchParams.get('page') || '1', 10),
    pageSize: parseInt(urlSearchParams.get('pageSize') || '10', 10),
    total: 0,
  });

  // 用于跟踪是否已经初始化
  const isInitialized = useRef(false);
  const [searchParams, setSearchParams] = useState<TrademarkListParams>({});

  // 排序状态管理
  const [sortConfigState, setSortConfig] = useState<{
    field?: string;
    order?: 'ascend' | 'descend';
  }>({});

  // 使用 useMemo 稳定 sortConfig 对象引用
  const sortConfig = useMemo(() => sortConfigState, [
    sortConfigState.field,
    sortConfigState.order,
  ]);

  const permissions = usePermissions();
  const { hasRole, state } = useAuth();
  const isAdmin = hasRole('admin');
  const isSales = hasRole('sales');
  const isCoordinator = hasRole('coordinator');
  const isSalesManager = hasRole('sales_manager');
  const isCoordinatorManager = hasRole('coordinator_manager');
  const currentUserId = state.user?.id;

  // 筛选状态
  const [filtersState, setFilters] = useState<{ search: string; nation: string; status: string; user_id: number | '' }>({
    search: '',
    nation: '',
    status: '',
    user_id: ''
  });

  // 使用 useMemo 稳定 filters 对象引用
  const filters = useMemo(() => filtersState, [
    filtersState.search,
    filtersState.nation,
    filtersState.status,
    filtersState.user_id,
  ]);

  const resolvedUserId = useMemo(() => {
    if (isAdmin) {
      return filters.user_id ? Number(filters.user_id) : undefined;
    }

    if (isSalesManager || isCoordinatorManager) {
      return filters.user_id ? Number(filters.user_id) : undefined;
    }

    if (isCoordinator) {
      return filters.user_id ? Number(filters.user_id) : undefined;
    }

    if (isSales) {
      return currentUserId;
    }

    return undefined;
  }, [filters.user_id, isAdmin, isSalesManager, isCoordinatorManager, isSales, isCoordinator, currentUserId]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTrademark, setEditingTrademark] = useState<Trademark | null>(null);
  const [statusDetailVisible, setStatusDetailVisible] = useState(false);
  const [statusDetailTrademark, setStatusDetailTrademark] = useState<Trademark | null>(null);
  const [tagModalVisible, setTagModalVisible] = useState(false);
  const [currentTrademark, setCurrentTrademark] = useState<Trademark | null>(null);
  const [tagInputValue, setTagInputValue] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tagSuggestions, setTagSuggestions] = useState<any[]>([]);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [form] = Form.useForm();
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const canCreate = permissions.hasActionAccess('trademark-monitor', 'create');
  const canEdit = permissions.hasActionAccess('trademark-monitor', 'edit');
  const canImport = permissions.hasActionAccess('trademark-monitor', 'import');
  const canExport = permissions.hasActionAccess('trademark-monitor', 'export');
  const canDelete = permissions.hasActionAccess('trademark-monitor', 'delete');
  const canUpdateStatus = permissions.hasActionAccess('trademark-monitor', 'update_status');

  // 行选择与批量删除
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const rowSelection = React.useMemo(() => ({
    selectedRowKeys,
    onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
    preserveSelectedRowKeys: true,
  }), [selectedRowKeys]);

  // 加载商标列表
  const loadTrademarks = async (params?: TrademarkListParams, options?: { showLoading?: boolean }) => {
    const showLoading = options?.showLoading ?? true;

    try {
      if (showLoading) {
        setLoading(true);
      }

      // 构建请求参数
      const requestParams: any = {
        page: pagination.current,
        per_page: pagination.pageSize,
        ...searchParams,
        ...params,
      };

      const requestUserId = typeof requestParams.user_id !== 'undefined'
        ? requestParams.user_id
        : resolvedUserId;

      if (typeof requestUserId !== 'undefined') {
        requestParams.user_id = requestUserId;
      }

      // 添加排序参数
      if (sortConfig.field) {
        requestParams.sort_by = sortConfig.field;
        requestParams.sort_direction = sortConfig.order === 'ascend' ? 'asc' : 'desc';
      }

      const response = await trademarkService.getTrademarks(requestParams);

      if (response.success) {
        setTrademarks(response.data.data);
        // 只更新总数，不更新分页状态
        setPagination(prev => ({
          ...prev,
          total: response.data.total,
        }));
      }
    } catch (error) {
      console.error('加载商标列表失败:', error);
      message.error('加载商标列表失败');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  // 所属人选项（管理员与主管）
  const [ownerOptions, setOwnerOptions] = useState<Array<{ value: number; label: string }>>([]);
  const ownerMapRef = useRef<Record<number, string>>({});

  const formatOwnerLabel = React.useCallback((label?: string) => {
    if (!label) return '';
    // 去掉 ()、<> 及其后内容
    const patterns = ['(', '<'];
    let cut = label.length;
    patterns.forEach(p => {
      const idx = label.indexOf(p);
      if (idx >= 0 && idx < cut) cut = idx;
    });
    const sliced = label.slice(0, cut).trim();
    return sliced || label;
  }, []);

  // 初始化加载
  useEffect(() => {
    loadPageSizeSettings();
    // 延迟标记初始化完成，确保分页状态已设置
    setTimeout(() => {
      isInitialized.current = true;
      // 初始化完成后获取第一次数据
      loadTrademarks();
    }, 0);
  }, []);

  // 加载所属人选项（仅管理员）
  useEffect(() => {
    const loadUsers = async () => {
      if (!(isAdmin || isSalesManager || isCoordinatorManager || isCoordinator)) return;
      try {
        const res = await trademarkService.getFilterOptions();
        const owners = res?.data?.owners || [];
        const m: Record<number, string> = {};
        const cleaned = owners.map((o: any) => {
          const label = formatOwnerLabel(o.label || '');
          m[o.value] = label;
          return { value: o.value, label };
        });
        setOwnerOptions(cleaned);
        ownerMapRef.current = m;
      } catch (e) {
        console.error('加载用户列表失败:', e);
      }
    };
    loadUsers();
  }, [isAdmin, isSalesManager, isCoordinatorManager, isCoordinator]);

  // 加载状态翻译字典
  const [statusTranslations, setStatusTranslations] = useState<Record<string, string>>({});
  useEffect(() => {
    const fetchTranslations = async () => {
      try {
        // 获取所有翻译，假设数量不超过 1000
        const res = await statusTranslationService.list({ per_page: 1000 });
        if (res.success && res.data?.data) {
          const map: Record<string, string> = {};
          res.data.data.forEach(item => {
            if (item.english && item.chinese) {
              // 翻译英文和爬虫状态必须使用相同的规范化键，避免 NBSP 等不可见空格导致匹配失败。
              map[normalizeTrademarkStatus(item.english)] = item.chinese;
            }
          });
          setStatusTranslations(map);
        }
      } catch (error) {
        console.error('Failed to load status translations', error);
      }
    };
    fetchTranslations();
  }, []);

  // 监听筛选条件变化，重置到第一页
  useEffect(() => {
    // 跳过初始化时的触发
    if (!isInitialized.current) return;

    const params = {
      search: filters.search || undefined,
      status: filters.status || undefined,
      nation: filters.nation || undefined,
      user_id: resolvedUserId,
    };
    setSearchParams(params);

    if (pagination.current !== 1) {
      const newPagination = { ...pagination, current: 1 };
      setPagination(newPagination);
      updateUrlParams(newPagination);
    } else {
      loadTrademarks({ ...params, page: 1 });
    }
  }, [filters, resolvedUserId]);

  // 分页变化时获取数据
  useEffect(() => {
    // 跳过初始化时的触发
    if (!isInitialized.current) return;

    if (pagination.current > 0 && pagination.pageSize > 0) {
      loadTrademarks({
        page: pagination.current,
        per_page: pagination.pageSize,
      });
    }
  }, [pagination.current, pagination.pageSize]);

  // 排序变化时获取数据
  useEffect(() => {
    // 跳过初始化时的触发
    if (!isInitialized.current) return;

    // 排序变化时从第一页开始
    if (pagination.current !== 1) {
      const newPagination = { ...pagination, current: 1 };
      setPagination(newPagination);
      updateUrlParams(newPagination);
    } else {
      loadTrademarks();
    }
  }, [sortConfig]);

  // 同步分页状态到 URL
  const updateUrlParams = (newPagination: { current: number; pageSize: number; total?: number }) => {
    const newSearchParams = new URLSearchParams(urlSearchParams);
    newSearchParams.set('page', newPagination.current.toString());
    newSearchParams.set('pageSize', newPagination.pageSize.toString());
    setUrlSearchParams(newSearchParams, { replace: true });
  };

  // 加载每页条数设置
  const loadPageSizeSettings = async () => {
    try {
      const savedPageSize = await userPreferenceService.getPageSizeSettings('trademarks');
      // 优先使用 URL 参数，如果没有则使用保存的偏好
      const urlPageSize = parseInt(urlSearchParams.get('pageSize') || '0', 10);
      const finalPageSize = urlPageSize > 0 ? urlPageSize : savedPageSize;

      // 只更新 pageSize，不更新 current，避免覆盖 URL 中的页码
      if (pagination.pageSize !== finalPageSize) {
        setPagination(prev => ({
          ...prev,
          pageSize: finalPageSize
        }));
        // 同步 pageSize 到 URL（保持当前页码不变）
        const newSearchParams = new URLSearchParams(urlSearchParams);
        newSearchParams.set('pageSize', finalPageSize.toString());
        setUrlSearchParams(newSearchParams, { replace: true });
      }
    } catch (error) {
      console.error('加载每页条数设置失败:', error);
    }
  };

  // 搜索处理（保留，用于导出功能）
  const handleSearch = (values: any) => {
    const params = {
      search: values.search,
      status: values.status,
      nation: values.nation,
      user_id: resolvedUserId,
    };
    setSearchParams(params);
    setPagination({ ...pagination, current: 1 });
    loadTrademarks({ ...params, page: 1 });
  };

  // 处理筛选变化
  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // 重置筛选
  const handleResetFilters = () => {
    setFilters({
      search: '',
      nation: '',
      status: '',
      user_id: '' as any,
    });
  };

  // 表格分页和排序变化
  const handleTableChange = async (paginationConfig: any, filters: any, sorter: any) => {
    const isPageSizeChanged = pagination.pageSize !== paginationConfig.pageSize;

    // 处理排序变化
    setSortConfig({
      field: Array.isArray(sorter) ? undefined : sorter.field as string,
      order: Array.isArray(sorter) ? undefined : (sorter.order || undefined),
    });

    // 如果每页条数发生变化，保存用户偏好并从第一页开始
    if (isPageSizeChanged) {
      try {
        await userPreferenceService.savePageSizeSettings('trademarks', paginationConfig.pageSize);
      } catch (error) {
        console.error('保存每页条数设置失败:', error);
      }

      const newPagination = {
        ...pagination,
        current: 1, // 从第一页开始
        pageSize: paginationConfig.pageSize,
      };
      setPagination(newPagination);
      updateUrlParams(newPagination);
    } else {
      // 只是翻页，直接更新页码
      const newPagination = {
        ...pagination,
        current: paginationConfig.current,
        pageSize: paginationConfig.pageSize,
      };
      setPagination(newPagination);
      updateUrlParams(newPagination);
    }
  };

  // 添加/编辑商标
  const handleSubmit = async (values: any) => {
    try {
      // 处理日期格式 - 后端期望的字段名是 application_at
      const submitData = {
        ...values,
        application_at: values.application_date ? values.application_date.format('YYYY-MM-DD') : null,
      };
      // 删除前端表单字段名，避免重复
      delete submitData.application_date;

      if (editingTrademark) {
        // 编辑模式：单个更新
        await trademarkService.updateTrademark(editingTrademark.id!, submitData);
        message.success('商标更新成功');
      } else {
        // 新增模式：支持批量添加（每行一个申请号）
        const applicationNos = String(values.application_no || '')
          .split('\n')
          .map((line: string) => line.trim())
          .filter((line: string) => line.length > 0);

        if (applicationNos.length === 0) {
          message.error('请输入至少一个申请号');
          return;
        }

        let successCount = 0;
        let failedCount = 0;
        const errors: string[] = [];

        for (const appNo of applicationNos) {
          try {
            await trademarkService.createTrademark({
              ...submitData,
              application_no: appNo,
            });
            successCount++;
          } catch (error: any) {
            failedCount++;
            const errMsg = error?.response?.data?.message || error?.message || '未知错误';
            errors.push(`${appNo}: ${errMsg}`);
          }
        }

        if (successCount > 0 && failedCount === 0) {
          message.success(`成功添加 ${successCount} 个商标监控`);
        } else if (successCount > 0 && failedCount > 0) {
          message.warning(`添加完成：成功 ${successCount} 个，失败 ${failedCount} 个`);
          if (errors.length > 0) {
            console.error('批量添加失败详情:', errors);
          }
        } else {
          message.error(`添加失败: ${errors[0] || '未知错误'}`);
          return;
        }
      }
      setModalVisible(false);
      setEditingTrademark(null);
      form.resetFields();
      loadTrademarks();
    } catch (error) {
      console.error('保存商标失败:', error);
      message.error('保存商标失败');
    }
  };

  // 删除商标
  const handleDelete = async (id: number) => {
    try {
      await trademarkService.deleteTrademark(id);
      message.success('商标删除成功');
      loadTrademarks();
    } catch (error) {
      console.error('删除商标失败:', error);
      message.error('删除商标失败');
    }
  };

  // 批量删除商标
  const handleBulkDelete = async () => {
    if (!selectedRowKeys.length) return;
    try {
      const ids = selectedRowKeys as number[];
      const response = await trademarkService.batchDeleteTrademarks(ids);
      if (response.success) {
        message.success(response.message || `成功删除 ${response.data?.deleted_count || ids.length} 个商标`);
        setSelectedRowKeys([]);
        loadTrademarks();
      } else {
        message.error(response.message || '批量删除失败');
      }
    } catch (e: any) {
      message.error(e?.response?.data?.message || '批量删除失败');
    }
  };

  // 同步商标状态
  const handleSyncStatus = async (id: number) => {
    try {
      setSyncingStatus(true);
      const response = await trademarkService.syncStatus(id);
      if (response.success) {
        // 根据是否跳过显示不同消息类型
        message.success(response.message || '商标状态同步成功');
        await loadTrademarks(undefined, { showLoading: false });
      } else {
        message.error(response.message || '同步失败');
      }
    } catch (error: any) {
      console.error('同步商标状态失败:', error);
      message.error(error?.response?.data?.message || '同步商标状态失败');
    } finally {
      setSyncingStatus(false);
    }
  };

  // 编辑商标
  const handleEdit = (record: Trademark) => {
    setEditingTrademark(record);
    // 处理日期字段的回显 - 后端返回的是 application_at
    const formData = {
      ...record,
      application_date: record.application_date || record.application_at ?
        dayjs(record.application_date || record.application_at) : null,
    };
    form.setFieldsValue(formData);
    setModalVisible(true);
  };

  // 显示状态详情
  const showStatusDetail = (record: Trademark) => {
    setStatusDetailTrademark(record);
    setStatusDetailVisible(true);
  };

  // 显示标签Modal
  const showTagModal = async (record: Trademark) => {
    setCurrentTrademark(record);
    setTagModalVisible(true);

    // 加载当前商标的标签
    if (record.id) {
      try {
        const response = await tagService.getTrademarkTags(record.id);
        if (response.success) {
          setSelectedTags(response.data.map(tag => tag.name));
        }
      } catch (error) {
        console.error('加载标签失败:', error);
      }
    }
  };

  // 搜索标签
  const searchTags = async (query: string) => {
    if (!query.trim()) {
      setTagSuggestions([]);
      return;
    }

    try {
      const response = await tagService.searchTags(query);
      if (response.success) {
        setTagSuggestions(response.data.map(tag => ({
          value: tag.name,
          label: tag.name,
          color: tag.color
        })));
      }
    } catch (error) {
      console.error('搜索标签失败:', error);
      setTagSuggestions([]);
    }
  };

  // 添加标签
  const addTag = async (tagName: string) => {
    const trimmedName = tagName.trim();
    if (!trimmedName || selectedTags.includes(trimmedName)) {
      setTagInputValue('');
      return;
    }

    // 检查是否是新标签（不在建议列表中）
    const existingTag = tagSuggestions.find(tag => tag.value === trimmedName);

    if (!existingTag) {
      // 如果是新标签，先创建标签
      try {
        await tagService.createTag(trimmedName);
        console.log(`自动创建新标签: ${trimmedName}`);
      } catch (error) {
        console.error('创建新标签失败:', error);
        // 即使创建失败，也允许添加到选中列表，因为后端会在保存时自动创建
      }
    }

    setSelectedTags([...selectedTags, trimmedName]);
    setTagInputValue('');
  };

  // 移除标签
  const removeTag = (tagName: string) => {
    setSelectedTags(selectedTags.filter(tag => tag !== tagName));
  };

  // 保存标签
  const saveTags = async () => {
    if (!currentTrademark?.id) return;

    // 用户输入新标签后可直接保存，无需先按 Enter 将其加入已选列表。
    const tagNames = buildTrademarkTagNames(selectedTags, tagInputValue);
    console.log('保存标签:', { trademarkId: currentTrademark.id, selectedTags: tagNames });

    try {
      const response = await tagService.attachTagsToTrademark(currentTrademark.id, tagNames);
      if (response.success) {
        // 空数组代表清除全部标签，保存后刷新列表以立即同步当前行展示。
        await loadTrademarks(undefined, { showLoading: false });
        message.success('标签保存成功');
        setTagModalVisible(false);
        setSelectedTags([]);
        setTagInputValue('');
        setCurrentTrademark(null);
      }
    } catch (error) {
      console.error('保存标签失败:', error);
      console.error('错误详情:', error);
      message.error('保存标签失败');
    }
  };

  // 导出数据
  const handleExport = async () => {
    try {
      const params = new URLSearchParams();

      // 添加当前的筛选条件
      if (searchParams.search) params.append('search', searchParams.search);
      if (searchParams.status) params.append('status', searchParams.status);
      if (searchParams.nation) params.append('nation', searchParams.nation);
      if ((isAdmin || isCoordinatorManager || isCoordinator) && (searchParams as any).user_id) {
        params.append('user_id', String((searchParams as any).user_id));
      }

      // 构建下载URL
      const token = localStorage.getItem('token');
      const url = `${process.env.REACT_APP_API_URL || 'http://trademark.test/api'}/trademarks-export?${params.toString()}`;

      // 创建临时链接下载
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('Authorization', `Bearer ${token}`);
      link.style.display = 'none';

      // 使用 fetch 下载文件
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      });

      if (!response.ok) {
        throw new Error('导出失败');
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      link.href = downloadUrl;
      link.download = `商标数据_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      message.success('导出成功');
    } catch (error) {
      console.error('导出失败:', error);
      message.error('导出失败');
    }
  };

  // 处理文件导入
  const handleImport = async (file: any) => {
    const formData = new FormData();
    formData.append('file', file);

    setImportLoading(true);
    setImportProgress(0);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://trademark.test/api'}/trademarks-import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const result = await response.json();

      if (result.success) {
        const { total, success, updated, failed, errors } = result.data;

        let messageText = `导入完成！总计: ${total}, 成功: ${success}, 更新: ${updated}`;
        if (failed > 0) {
          messageText += `, 失败: ${failed}`;
        }

        message.success(messageText);

        // 如果有错误，显示详细错误信息
        if (errors && errors.length > 0) {
          console.error('导入错误详情:', errors);
          Modal.error({
            title: '部分数据导入失败',
            content: (
              <div>
                <p>以下数据导入失败，请检查后重新导入：</p>
                <ul style={{ maxHeight: '200px', overflow: 'auto' }}>
                  {errors.slice(0, 5).map((error: any, index: number) => (
                    <li key={index}>第{error.row}行: {error.error}</li>
                  ))}
                  {errors.length > 5 && <li>... 还有 {errors.length - 5} 个错误</li>}
                </ul>
              </div>
            ),
            width: 600
          });
        }

        // 重新加载列表
        loadTrademarks();
      } else {
        message.error(result.message || '导入失败');
      }
    } catch (error) {
      console.error('导入失败:', error);
      message.error('导入失败，请检查文件格式');
    } finally {
      setImportLoading(false);
      setImportProgress(0);
      setImportModalVisible(false);
    }

    return false; // 阻止默认上传行为
  };

  // 处理申请号点击


  // 所有可用的列定义
  const allColumnDefinitions: Record<string, any> = React.useMemo(() => ({
    application_no: {
      title: '申请号',
      dataIndex: 'application_no',
      key: 'application_no',
      width: 180,
      ellipsis: true,
      render: (applicationNo: string, record: Trademark) => {
        const officeName = getTrademarkOfficeName(record.nation || '');

        const url = getTrademarkUrl({
          nation: record.nation || '',
          applicationNo: record.application_no || '',
        });

        if (url) {
          return (
            <Tooltip title={`在新窗口打开 ${officeName} 查看详情：${applicationNo}`}>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontSize: 'inherit',
                  textDecoration: 'underline',
                  color: '#1890ff',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'inline-block',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {applicationNo}
              </a>
            </Tooltip>
          );
        }

        return (
          <Tooltip title={applicationNo}>
            <span style={{
              display: 'inline-block',
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>{applicationNo || '-'}</span>
          </Tooltip>
        );
      },
    },
    nation: {
      title: '国家/地区',
      dataIndex: 'nation',
      key: 'nation',
      width: 100,
    },
    registration_no: {
      title: '注册号',
      dataIndex: 'registration_no',
      key: 'registration_no',
      width: 100,
    },
    trademark_name: {
      title: '商标名称',
      dataIndex: 'name',
      key: 'trademark_name',
      width: 150,
      ellipsis: true,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
    },
    image: {
      title: '图样',
      dataIndex: 'image',
      key: 'image',
      width: 120,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      render: (imageUrl: string) => {
        if (!imageUrl) return '-';

        return (
          <div style={{ position: 'relative' }}>
            <img
              src={imageUrl}
              alt="商标图样"
              loading="lazy"
              style={{
                width: '64px',
                maxWidth: '64px',
                height: '48px',
                objectFit: 'contain',
                border: '1px solid #f0f0f0',
                borderRadius: '4px',
                backgroundColor: '#fff',
                cursor: 'pointer'
              }}
              title="点击查看大图"
              onClick={() => window.open(imageUrl, '_blank')}
              onError={(e) => {
                // 图片加载失败时显示占位符
                const target = e.currentTarget as HTMLImageElement;
                target.style.display = 'none';
                const parent = target.parentElement;
                if (parent) {
                  parent.innerHTML = '<div style="width: 64px; height: 48px; border: 1px solid #f0f0f0; border-radius: 4px; display: flex; align-items: center; justify-content: center; color: #999; font-size: 12px; background: #fafafa;">无图片</div>';
                }
              }}
            />
          </div>
        );
      },
    },
    primary_class: {
      title: '大类',
      dataIndex: 'primary_class',
      key: 'primary_class',
      width: 120,
    },
    sub_class: {
      title: '小类',
      dataIndex: 'sub_class',
      key: 'sub_class',
      width: 120,
      render: (subClass: string[] | string) => {
        // 后端已经反序列化为数组
        let classList: string[] = [];

        if (Array.isArray(subClass)) {
          classList = subClass;
        } else if (typeof subClass === 'string' && subClass) {
          classList = [subClass];
        } else {
          return '-';
        }

        // 过滤空值
        classList = classList.filter(item => item && item.trim());

        if (classList.length === 0) return '-';

        // 创建tooltip内容
        const tooltipContent = (
          <div style={{ maxWidth: 400 }}>
            <div style={{
              fontWeight: 'bold',
              marginBottom: 8,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span>小类列表 ({classList.length} 项)：</span>
              <Button
                type="link"
                size="small"
                style={{ color: '#1890ff', padding: 0 }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => {
                  // 阻止事件冒泡，防止触发外层点击事件
                  e.stopPropagation();
                  // 阻止默认行为（虽然 Button type=button 没有默认行为，但作为保险）
                  e.preventDefault();

                  const text = classList.join('\n');
                  if (navigator.clipboard) {
                    navigator.clipboard.writeText(text).then(() => {
                      message.success('复制成功');
                    }).catch(() => {
                      message.error('复制失败');
                    });
                  } else {
                    // Fallback
                    const textarea = document.createElement('textarea');
                    textarea.value = text;
                    document.body.appendChild(textarea);
                    textarea.select();
                    try {
                      document.execCommand('copy');
                      message.success('复制成功');
                    } catch (err) {
                      message.error('复制失败');
                    }
                    document.body.removeChild(textarea);
                  }
                }}
              >
                复制全部
              </Button>
            </div>
            <div style={{ maxHeight: 300, overflowY: 'auto' }}>
              <ol style={{
                margin: 0,
                paddingLeft: '20px',
                listStyleType: 'decimal'
              }}>
                {classList.map((item, index) => (
                  <li key={index} style={{
                    marginBottom: '8px',
                    marginLeft: '8px',
                    lineHeight: '1.5',
                  }}>
                    {item}
                  </li>
                ))}
              </ol>
            </div>
          </div>
        );

        return (
          <Popover
            content={tooltipContent}
            placement="topLeft"
            trigger="click"
            overlayStyle={{ maxWidth: 450 }}
          >
            <Button
              type="link"
              size="small"
              style={{
                padding: 0,
                height: 'auto',
                color: '#1890ff',
                textDecoration: 'underline'
              }}
            >
              查看({classList.length})
            </Button>
          </Popover>
        );
      },
    },
    owner_name: {
      title: '申请人',
      dataIndex: 'owner_name',
      key: 'owner_name',
      width: 160,
      ellipsis: true,
      render: (value: string) => value ? (
        <span
          style={{ cursor: 'pointer' }}
          title={value}
          onClick={(e) => handleCopyText(value, e)}
        >
          {value}
        </span>
      ) : '-',
    },
    owner_username: {
      title: '所属人',
      dataIndex: 'owner_username',
      key: 'owner_username',
      width: 150,
      ellipsis: true,
    },
    user_email: {
      title: '客户邮箱',
      dataIndex: 'user_email',
      key: 'user_email',
      width: 150,
      ellipsis: true,
    },
    attorney_name: {
      title: '代理律师',
      dataIndex: 'attorney_name',
      key: 'attorney_name',
      width: 150,
      ellipsis: true,
      render: (value: string) => value ? (
        <span
          style={{ cursor: 'pointer' }}
          title={value}
          onClick={(e) => handleCopyText(value, e)}
        >
          {value}
        </span>
      ) : '-',
    },
    attorney_email: {
      title: '代理律师邮箱',
      dataIndex: 'attorney_email',
      key: 'attorney_email',
      width: 150,
      ellipsis: true,
      render: (value: string) => value ? (
        <span
          style={{ cursor: 'pointer' }}
          title={value}
          onClick={(e) => handleCopyText(value, e)}
        >
          {value}
        </span>
      ) : '-',
    },
    correspondent_name_address: {
      title: '通讯人',
      dataIndex: 'correspondent_name_address',
      key: 'correspondent_name_address',
      width: 150,
      ellipsis: true,
      render: (value: string) => value || '-',
    },
    correspondent_email: {
      title: '通讯邮箱',
      dataIndex: 'correspondent_email',
      key: 'correspondent_email',
      width: 150,
      ellipsis: true,
      render: (value: string) => value || '-',
    },
    remote_status: {
      title: '商标局状态',
      dataIndex: 'remote_status',
      key: 'remote_status',
      width: 150,
      ellipsis: true,
      render: (value: string) => {
        if (!value) return '-';
        const displayStatus = statusTranslations[normalizeTrademarkStatus(value)] || value;
        return <span title={displayStatus}>{displayStatus}</span>;
      },
    },
    status: {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 200,
      render: (status: string, record: Trademark) => {
        // 获取历史数据的第一条记录（后端已反序列化）
        let firstHistoryRecord = null;
        if (record.history && Array.isArray(record.history) && record.history.length > 0) {
          firstHistoryRecord = record.history[0];
        }

        const displayDate = firstHistoryRecord?.date || record.application_at;
        const rawStatus = firstHistoryRecord?.record || status;
        const displayStatus = (rawStatus && statusTranslations[normalizeTrademarkStatus(rawStatus)]) || rawStatus;

        return (
          <div style={{ lineHeight: '1.4' }}>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>
              {displayDate ? dayjs(displayDate).format('YYYY-MM-DD') : '-'}
            </div>
            <div style={{ marginBottom: '4px', fontSize: '13px', color: '#333' }}>
              {displayStatus || '-'}
            </div>
            <Button
              type="link"
              size="small"
              style={{
                padding: 0,
                height: 'auto',
                fontSize: '12px',
                color: '#1890ff'
              }}
              onClick={() => showStatusDetail(record)}
            >
              查看详情
            </Button>
          </div>
        );
      },
    },
    tags: {
      title: '标签',
      dataIndex: 'tags',
      key: 'tags',
      width: 150,
      render: (tags: { id: number; name: string; color: string }[]) => {
        if (!tags || tags.length === 0) return '-';
        return (
          <Space size={[0, 4]} wrap>
            {tags.map(tag => (
              <Tag key={tag.id} color={tag.color || '#1890ff'}>
                {tag.name}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    created_at: {
      title: '添加日期',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD') : '-',
    },
    application_date: {
      title: '申请日期',
      dataIndex: 'application_at',
      key: 'application_date',
      width: 120,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD') : '-',
    },
    status_updated_at: {
      title: '状态更新日期',
      dataIndex: 'status_updated_at',
      key: 'status_updated_at',
      width: 120,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD') : '-',
    },
    sync_at: {
      title: '上次抓取日期',
      dataIndex: 'sync_at',
      key: 'sync_at',
      width: 120,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-',
    },
    due_at: {
      title: '截止日期',
      dataIndex: 'due_at',
      key: 'due_at',
      width: 120,
      render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD') : '-',
    },
    next_update_at: {
      title: '预计下次更新',
      dataIndex: 'next_update_at',
      key: 'next_update_at',
      width: 120,
      render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD') : '-',
    },
    approve_at: {
      title: '注册日期',
      dataIndex: 'approve_at',
      key: 'approve_at',
      width: 120,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD') : '-',
    },
    actions: {
      title: '操作',
      key: 'actions',
      width: 150,
      fixed: 'right' as const,
      render: (_: any, record: Trademark) => (
        <Space size="small">
          {canUpdateStatus && (
            <Button
              type="primary"
              size="small"
              icon={<SyncOutlined />}
              onClick={() => record.id && handleSyncStatus(record.id)}
            >

            </Button>
          )}
          {canEdit && (
            <Button
              size="small"
              icon={<TagOutlined />}
              onClick={() => showTagModal(record)}
            >

            </Button>
          )}
          {canEdit && (
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            >

            </Button>
          )}
          {canDelete && (
            <Popconfirm
              title="确定删除这个商标吗？"
              onConfirm={() => record.id && handleDelete(record.id)}
              okText="确定"
              cancelText="取消"
            >
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
              >

              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  }), [canDelete, canEdit, canUpdateStatus, handleDelete, handleEdit, handleSyncStatus, showTagModal, statusTranslations]);

  // 兼容旧列键并去重，保证只有定义过的列被使用
  const normalizeVisibleColumns = React.useCallback((columns: string[]) => {
    const legacyMap: Record<string, string> = {
      name: 'trademark_name',
      user_id: 'owner_username',
    };

    const normalized: string[] = [];

    columns.forEach((key) => {
      const mappedKey = legacyMap[key] || key;
      if (allColumnDefinitions[mappedKey] && !normalized.includes(mappedKey)) {
        normalized.push(mappedKey);
      }
    });

    return normalized;
  }, [allColumnDefinitions]);

  // 默认显示的列
  const defaultVisibleColumns = React.useMemo(() => [
    'application_no', 'nation', 'trademark_name', 'image', 'primary_class',
    'sub_class', 'owner_name', 'owner_username', 'status', 'tags', 'created_at', 'application_date',
    'actions'
  ], []);

  const columnsLoadedRef = React.useRef(false);

  // 初始化可见列
  React.useEffect(() => {
    if (columnsLoadedRef.current) return;
    columnsLoadedRef.current = true;
    const loadColumnSettings = async () => {
      try {
        const savedColumns = await userPreferenceService.getColumnSettings('trademarks');
        const columnKeys = savedColumns && savedColumns.length > 0
          ? savedColumns
          : defaultVisibleColumns;
        setVisibleColumns(normalizeVisibleColumns(columnKeys));
      } catch (error) {
        console.warn('加载列设置失败:', error);
        setVisibleColumns(normalizeVisibleColumns(defaultVisibleColumns));
      }
    };

    loadColumnSettings();
  }, [normalizeVisibleColumns, defaultVisibleColumns]);

  // 获取当前要显示的列
  const columns: ColumnsType<Trademark> = React.useMemo(() => {
    const columnsToShow = visibleColumns.length > 0 ? visibleColumns : defaultVisibleColumns;
    if (!Array.isArray(columnsToShow)) return [];
    const hasRowActionAccess = canEdit || canDelete || canUpdateStatus;
    const candidate = columnsToShow
      .map((key: string) => ({ key, ...allColumnDefinitions[key] }))
      .filter(col => !!col && !!col.key && (col.key !== 'actions' || hasRowActionAccess));
    const permitted = permissions.getVisibleColumns('trademark-monitor', candidate as any);
    return permitted.map((c: any) => ({ ...c }));
  }, [visibleColumns, defaultVisibleColumns, allColumnDefinitions, canEdit, canDelete, canUpdateStatus, permissions]);

  // 获取所有可用列信息
  const availableColumns = permissions.getVisibleColumns('trademark-monitor',
    Object.keys(allColumnDefinitions).map(key => ({
      key,
      title: allColumnDefinitions[key].title,
      fixed: allColumnDefinitions[key].fixed
    })).filter(column => column.key !== 'actions' || canEdit || canDelete || canUpdateStatus) as any
  );

  // 处理列显示变更
  const handleColumnChange = async (columns: string[]) => {
    // 保证 actions 固定在末尾
    const normalized = normalizeVisibleColumns(columns);
    const withoutActions = normalized.filter(k => k !== 'actions');

    // 保留用户拖拽设置的顺序，不再重新按默认顺序排序
    const finalCols = [...withoutActions, 'actions'];

    setVisibleColumns(finalCols);
    try {
      await userPreferenceService.saveColumnSettings('trademarks', finalCols);
    } catch (error) {
      console.error('保存列设置失败:', error);
      message.error('保存列设置失败');
    }
  };

  return (
    <div className="trademark-monitor-page" style={{ padding: '24px' }}>
      {syncingStatus && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(255, 255, 255, 0.45)',
            pointerEvents: 'auto',
            cursor: 'wait',
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <Spin />
            <div style={{ marginTop: 12, color: '#333' }}>正在更新商标信息...</div>
          </div>
        </div>
      )}
      <Card
        title={(
          <Space size="small">
            <span>商标监控</span>
            <ListHelpButton page_key="trademark_monitor" />
          </Space>
        )}
        extra={
          <Space>
            <ColumnSelector
              availableColumns={availableColumns}
              visibleColumns={visibleColumns}
              onColumnChange={handleColumnChange}
              fixedColumns={['actions']}
            />
            {canCreate && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setEditingTrademark(null);
                  form.resetFields();
                  setModalVisible(true);
                }}
                data-testid="btn-tm-add"
              >
                添加商标
              </Button>
            )}
            {(() => {
              const items: any[] = [];
              if (canExport) items.push({ key: 'export', label: '导出数据', icon: <DownloadOutlined />, onClick: handleExport });
              if (canImport) items.push({ key: 'import', label: '导入数据', icon: <UploadOutlined />, onClick: () => setImportModalVisible(true) });
              return items.length > 0 ? (
                <Dropdown menu={{ items }}>
                  <Button icon={<FileExcelOutlined />} data-testid="btn-tm-data-menu">数据管理</Button>
                </Dropdown>
              ) : null;
            })()}
            {canDelete && selectedRowKeys.length > 0 && (
              <Popconfirm
                title={`确定删除选中的 ${selectedRowKeys.length} 条记录吗？`}
                okText="确定"
                cancelText="取消"
                onConfirm={handleBulkDelete}
                disabled={!selectedRowKeys.length}
              >
                <Button danger disabled={!selectedRowKeys.length}>批量删除</Button>
              </Popconfirm>
            )}
          </Space>
        }
      >
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          {/* 左侧筛选表单 */}
          <Col span={24}>
            <Space>
              <Input
                placeholder="搜索商标名称、申请号"
                style={{ width: 200 }}
                prefix={<SearchOutlined />}
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                allowClear
                data-testid="tm-search"
              />
              <Select
                placeholder="选择国家/地区"
                style={{ width: 200 }}
                allowClear
                showSearch
                value={filters.nation || undefined}
                onChange={(value) => handleFilterChange('nation', value || '')}
                filterOption={(input, option) =>
                  String(option?.children || '').toLowerCase().includes(input.toLowerCase())
                }
                data-testid="tm-filter-nation"
              >
                {MONITORED_NATION_OPTIONS.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
              <Select
                placeholder="选择状态"
                style={{ width: 120 }}
                allowClear
                showSearch
                value={filters.status || undefined}
                onChange={(value) => handleFilterChange('status', value || '')}
                data-testid="tm-filter-status"
              >
                <Option value="待审查">待审查</Option>
                <Option value="审查中">审查中</Option>
                <Option value="已注册">已注册</Option>
                <Option value="已驳回">已驳回</Option>
              </Select>
              {(isAdmin || isSalesManager || isCoordinatorManager || isCoordinator) && (
                <Select
                  placeholder="所属销售"
                  style={{ width: 300 }}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  value={(filters.user_id as number) || undefined}
                  onChange={(value) => handleFilterChange('user_id', (value || '') as any)}
                  data-testid="tm-filter-owner"
                >
                  {ownerOptions.map((o) => (
                    <Option key={o.value} value={o.value}>
                      {o.label}
                    </Option>
                  ))}
                </Select>
              )}
            </Space>
          </Col>
        </Row>

        {/* 筛选状态显示 */}
        {Object.values(filters).some(value => value) && (
          <Row style={{ marginBottom: 16 }}>
            <Col span={24}>
              <div style={{
                padding: '8px 12px',
                backgroundColor: '#f0f2f5',
                borderRadius: '6px',
                fontSize: '14px',
                color: '#666'
              }}>
                <Space wrap>
                  <span>已应用筛选条件：</span>
                  {filters.search && (
                    <Tag closable onClose={() => handleFilterChange('search', '')}>
                      搜索: {filters.search}
                    </Tag>
                  )}
                  {filters.nation && (
                    <Tag closable onClose={() => handleFilterChange('nation', '')}>
                      国家: {MONITORED_NATION_OPTIONS.find(opt => opt.value === filters.nation)?.label || filters.nation}
                    </Tag>
                  )}
                  {filters.status && (
                    <Tag closable onClose={() => handleFilterChange('status', '')}>
                      状态: {filters.status}
                    </Tag>
                  )}
                  {(isAdmin || isSalesManager || isCoordinatorManager || isCoordinator) && filters.user_id && (
                    <Tag closable onClose={() => handleFilterChange('user_id', '' as any)}>
                      所属人: {ownerMapRef.current[Number(filters.user_id)] || filters.user_id}
                    </Tag>
                  )}
                  <Button type="link" size="small" onClick={handleResetFilters}>
                    清除所有筛选
                  </Button>
                </Space>
              </div>
            </Col>
          </Row>
        )}

        <Table
          columns={columns}
          dataSource={trademarks}
          rowKey="id"
          size="small"
          scroll={{ x: 1800 }}
          sticky={{ offsetHeader: 0 }}
          rowSelection={canDelete ? (rowSelection as any) : undefined}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
          loading={loading}
          onChange={handleTableChange}
        />
      </Card>

      <Modal
        title={editingTrademark ? '编辑商标' : '添加商标'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingTrademark(null);
          form.resetFields();
        }}
        footer={null}
        width={600}
        data-testid="modal-tm"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="nation"
                label="国家/地区"
                rules={[{ required: true, message: '请选择国家/地区' }]}
              >
                <Select
                  showSearch
                  filterOption={(input, option) =>
                    String(option?.children || '').toLowerCase().includes(input.toLowerCase())
                  }
                  data-testid="tm-nation"
                >
                  {MONITORED_NATION_OPTIONS.map(option => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="application_no"
                label={editingTrademark ? '申请号' : '申请号（每行一个）'}
                rules={[{ required: true, message: '请输入申请号' }]}
              >
                {editingTrademark ? (
                  <Input data-testid="tm-application-no" />
                ) : (
                  <Input.TextArea
                    rows={4}
                    placeholder="每行输入一个申请号"
                    data-testid="tm-application-no"
                  />
                )}
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                label="通知邮箱"
                name="user_email"
                extra="多个邮箱用 ; 分隔开"
                rules={[{ required: true, message: '请输入通知邮箱' }]}
              >
                <Input placeholder="例如: a@example.com; b@example.com" data-testid="tm-user-email" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" data-testid="btn-tm-submit">
                {editingTrademark ? '更新' : '添加监控'}
              </Button>
              <Button onClick={() => {
                setModalVisible(false);
                setEditingTrademark(null);
                form.resetFields();
              }} data-testid="btn-tm-cancel">
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 状态详情Modal */}
      <Modal
        title="实时状态"
        open={statusDetailVisible}
        onCancel={() => {
          setStatusDetailVisible(false);
          setStatusDetailTrademark(null);
        }}
        footer={null}
        width={1000}
        style={{ top: 20 }}
      >
        {statusDetailTrademark && (
          <Tabs
            defaultActiveKey="history"
            items={[
              {
                key: 'history',
                label: (
                  <span>
                    <EyeOutlined />
                    状态
                  </span>
                ),
                children: <HistoryTable trademark={statusDetailTrademark} statusTranslations={statusTranslations} />,
              },
              {
                key: 'documents',
                label: (
                  <span>
                    <EyeOutlined />
                    文档
                  </span>
                ),
                children: <DocumentsTable trademark={statusDetailTrademark} statusTranslations={statusTranslations} />,
              },
            ]}
          />
        )}
      </Modal>

      {/* 贴标签Modal */}
      <Modal
        title="贴标签"
        open={tagModalVisible}
        onOk={saveTags}
        onCancel={() => {
          setTagModalVisible(false);
          setSelectedTags([]);
          setTagInputValue('');
          setCurrentTrademark(null);
        }}
        okText="保存"
        cancelText="取消"
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8, fontSize: 14, fontWeight: 500 }}>
            请选择或输入标签
          </div>
          <AutoComplete
            value={tagInputValue}
            options={tagSuggestions}
            onSearch={searchTags}
            onChange={setTagInputValue}
            onSelect={addTag}
            placeholder="选择或输入标签"
            style={{ width: '100%' }}
          >
            <Input
              onPressEnter={async () => {
                if (tagInputValue.trim()) {
                  await addTag(tagInputValue);
                }
              }}
            />
          </AutoComplete>
        </div>

        {selectedTags.length > 0 && (
          <div>
            <div style={{ marginBottom: 8, fontSize: 14, fontWeight: 500 }}>
              已选择的标签：
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {selectedTags.map((tagName, index) => (
                <Tag
                  key={index}
                  closable
                  onClose={() => removeTag(tagName)}
                  style={{ marginBottom: 4 }}
                >
                  {tagName}
                </Tag>
              ))}
            </div>
          </div>
        )}
      </Modal>

      {/* 导入数据Modal */}
      <Modal
        title="导入商标数据"
        open={importModalVisible}
        onCancel={() => {
          setImportModalVisible(false);
          setImportProgress(0);
        }}
        footer={null}
        width={500}
      >
        <div style={{ padding: '20px 0' }}>
          <div style={{ marginBottom: 16 }}>
            <ul style={{ margin: 0, paddingLeft: '20px', color: '#666' }}>
              <li>支持 .xlsx、.xls、.csv 格式文件</li>
              <li>文件大小不能超过 10MB</li>
              <li>以「申请号 + 国家」为唯一性判断条件</li>
              <li>如果记录已存在，将会更新现有数据</li>
            </ul>
          </div>

          {importProgress > 0 && (
            <div style={{ marginBottom: 16 }}>
              <Progress
                percent={importProgress}
                status={importLoading ? 'active' : 'success'}
                size="small"
              />
            </div>
          )}

          <Upload.Dragger
            name="file"
            multiple={false}
            accept=".xlsx,.xls,.csv"
            beforeUpload={handleImport}
            showUploadList={false}
            disabled={importLoading}
          >
            <p className="ant-upload-drag-icon">
              <UploadOutlined style={{ fontSize: '48px', color: importLoading ? '#ccc' : '#1890ff' }} />
            </p>
            <p className="ant-upload-text" style={{ color: importLoading ? '#ccc' : '#333' }}>
              {importLoading ? '正在导入中...' : '点击或拖拽文件到这里进行导入'}
            </p>
            <p className="ant-upload-hint" style={{ color: '#999' }}>
              支持 Excel (.xlsx, .xls) 和 CSV 格式文件
            </p>
          </Upload.Dragger>

          <div style={{ marginTop: 16, textAlign: 'right' }}>
            <Space>
              <Button
                onClick={() => {
                  setImportModalVisible(false);
                  setImportProgress(0);
                }}
                disabled={importLoading}
              >
                取消
              </Button>
            </Space>
          </div>
        </div>
      </Modal>
    </div>
  );
};

// 历史记录表格组件
const HistoryTable: React.FC<{ trademark: Trademark; statusTranslations: Record<string, string> }> = ({ trademark, statusTranslations }) => {
  const [historyData, setHistoryData] = useState<any[]>([]);
  const [isTranslated, setIsTranslated] = useState(true);

  useEffect(() => {
    // 后端已反序列化，直接使用
    if (Array.isArray(trademark.history)) {
      setHistoryData(trademark.history);
    } else {
      setHistoryData([]);
    }
  }, [trademark.history]);

  // 切换语言
  const toggleLanguage = async () => {
    setIsTranslated(!isTranslated);
  };

  // 翻译状态文本
  const translateStatus = (originalStatus: string) => {
    if (!isTranslated || !originalStatus) {
      return originalStatus;
    }
    return statusTranslations[normalizeTrademarkStatus(originalStatus)] || originalStatus;
  };

  const columns = [
    {
      title: '日期',
      dataIndex: 'date',
      key: 'date',
      width: 120,
      render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD') : '-',
    },
    {
      title: '状态',
      dataIndex: 'record',
      key: 'record',
      render: (record: string) => translateStatus(record) || '-',
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontWeight: 500, fontSize: 16 }}>状态历史</div>
        <Button
          type="text"
          icon={<TranslationOutlined />}
          onClick={toggleLanguage}
          size="small"
        >
          {isTranslated ? '英文' : '中文'}
        </Button>
      </div>
      <Table
        dataSource={historyData}
        columns={columns}
        pagination={false}
        rowKey={(record, index) => index || 0}
        scroll={{ y: 400 }}
        size="small"
      />
    </div>
  );
};

// 文档记录表格组件
const DocumentsTable: React.FC<{ trademark: Trademark; statusTranslations: Record<string, string> }> = ({ trademark, statusTranslations }) => {
  const [documentsData, setDocumentsData] = useState<any[]>([]);
  const [isTranslated, setIsTranslated] = useState(true);

  useEffect(() => {
    // 后端已反序列化，直接使用
    if (Array.isArray(trademark.documents)) {
      setDocumentsData(trademark.documents);
    } else {
      setDocumentsData([]);
    }
  }, [trademark.documents]);

  // 切换语言
  const toggleLanguage = async () => {
    setIsTranslated(!isTranslated);
  };

  // 翻译状态文本
  const translateStatus = (originalStatus: string) => {
    if (!isTranslated || !originalStatus) {
      return originalStatus;
    }
    return statusTranslations[normalizeTrademarkStatus(originalStatus)] || originalStatus;
  };

  const columns = [
    {
      title: '日期',
      dataIndex: 'date',
      key: 'date',
      width: 120,
      render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD') : '-',
    },
    {
      title: '状态',
      dataIndex: 'description',
      key: 'description',
      width: 200,
      render: (status: string) => translateStatus(status) || '-',
    },
    {
      title: '商标文书',
      dataIndex: 'href',
      key: 'href',
      render: (href: string, record: any) => {
        // 只有美国商标才添加 USPTO 前缀
        if (trademark.nation === '美国' && href && !href.includes('https://tsdr.uspto.gov')) {
          href = 'https://tsdr.uspto.gov' + href;
        }
        if (href && href.trim()) {
          return (
            <Button
              type="link"
              size="small"
              onClick={() => window.open(href, '_blank')}
              style={{ padding: 0, height: 'auto', color: '#1890ff' }}
            >
              查看
            </Button>
          );
        }
        return '-';
      },
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontWeight: 500, fontSize: 16 }}>文档列表</div>
        <Button
          type="text"
          icon={<TranslationOutlined />}
          onClick={toggleLanguage}
          size="small"
        >
          {isTranslated ? '英文' : '中文'}
        </Button>
      </div>
      <Table
        dataSource={documentsData}
        columns={columns}
        pagination={false}
        rowKey={(record, index) => index || 0}
        scroll={{ y: 400 }}
        size="small"
      />
    </div>
  );
};

export default TrademarkMonitor;
